/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions */
import "./hub.css";

import { MotionConfig, useInView } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ctaDestination, trackEvent } from "@/client/analytics/track";
import { useAuthStore } from "@/client/stores/auth";
import { apex25HomeUrl, candidateToolUrl, firstRoundPublicUrl } from "@/client/utils/hubUrls";

import { HubFooter, HubHeader } from "./chrome";
import { candidate, finale, hero, HUB_META, recruiter, type Side, type Tool } from "./content";
import { Entry } from "./entry";
import { Panel } from "./panel";
import { ThreadSection } from "./thread";
import { Word } from "./word";

const LAST_SIDE_KEY = "hub:last-side";

// A visitor's last choice is a per-viewer convenience: it only tints the panel they used before.
const readLastSide = (): Side | undefined => {
  try {
    const value = window.localStorage.getItem(LAST_SIDE_KEY);
    return value === "candidate" || value === "recruiter" ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeLastSide = (side: Side) => {
  try {
    window.localStorage.setItem(LAST_SIDE_KEY, side);
  } catch {
    // Storage can be blocked (private windows); the hub works without it.
  }
};

const Pointer = () => (
  <svg width="18" height="20" viewBox="0 0 16 18" fill="currentColor" aria-hidden="true">
    <path d="M1 1l13 6.2-5.6 1.7L6.6 14.7z" />
  </svg>
);

const Cursors = () => (
  <>
    <span className="hub-cursor hub-cursor--candidate" aria-hidden="true">
      <span className="hub-cursor__drift">
        <Pointer />
        <em>Job seeker</em>
      </span>
    </span>
    <span className="hub-cursor hub-cursor--recruiter" aria-hidden="true">
      <span className="hub-cursor__drift">
        <Pointer />
        <em>Hiring team</em>
      </span>
    </span>
  </>
);

const DASHBOARD_PATHS: Record<string, string> = {
  resume: "/dashboard/resumes",
  spotlight: "/dashboard/spotlight-intro",
};

export const HubPage = () => {
  const signedIn = useAuthStore((state) => Boolean(state.user));
  const [lastSide, setLastSide] = useState<Side | undefined>();
  // The hub's own entrance animations wait until it scrolls into view.
  const heroRef = useRef<HTMLElement>(null);
  const heroSeen = useInView(heroRef, { amount: 0.12, once: true });

  useEffect(() => setLastSide(readLastSide()), []);

  useEffect(() => {
    const previous = document.title;
    document.title = HUB_META.title;
    return () => {
      document.title = previous;
    };
  }, []);

  const choose = useCallback((side: Side) => writeLastSide(side), []);

  const candidateHref = (tool: Tool) =>
    tool.lives === "brand"
      ? firstRoundPublicUrl()
      : candidateToolUrl(DASHBOARD_PATHS[tool.id] ?? "/dashboard", signedIn);

  return (
    <MotionConfig reducedMotion="user">
      <div className="hub">
        <HubHeader />
        <main>
          <Entry lastSide={lastSide} onChoose={choose} />
          <section
            id="hub-hero"
            ref={heroRef}
            className="hub-hero"
            data-in={heroSeen}
            aria-labelledby="hub-fork-title"
          >
            <div className="hub-shell">
              <div className="hub-hero__head">
                <h2 id="hub-fork-title" className="hub-hero__title">
                  <span className="hub-h1__line">
                    <Word index={0}>{hero.titleTop.split(" ")[0]}</Word>{" "}
                    <Word index={1}>{hero.titleTop.split(" ")[1]}</Word>{" "}
                    <Word index={2}>{hero.titleTop.split(" ")[2]}</Word>
                  </span>{" "}
                  <span className="hub-h1__line">
                    <Word index={3}>the</Word>{" "}
                    <Word index={4}>
                      <span className="hub-same">
                        same
                        <svg
                          className="hub-underline"
                          viewBox="0 0 120 14"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <path d="M3 9 C 26 2, 48 13, 70 6 S 104 3, 117 8" pathLength="1" />
                        </svg>
                      </span>
                    </Word>{" "}
                    <Word index={5}>interview.</Word>
                  </span>
                </h2>
                <p className="hub-hero__lead">{hero.lead}</p>
                <Cursors />
              </div>

              <div className="hub-fork">
                <Panel
                  data={candidate}
                  hrefFor={candidateHref}
                  ctaHref={firstRoundPublicUrl()}
                  more={{ label: candidate.more, to: "/job-seekers" }}
                  lastVisited={lastSide === "candidate"}
                  onChoose={choose}
                />
                <Panel
                  data={recruiter}
                  hrefFor={() => apex25HomeUrl()}
                  ctaHref={apex25HomeUrl()}
                  lastVisited={lastSide === "recruiter"}
                  onChoose={choose}
                />
              </div>
            </div>
          </section>

          <ThreadSection />

          <section className="hub-finale" aria-labelledby="hub-finale-title">
            <div className="hub-shell hub-finale__grid">
              <div>
                <h2 id="hub-finale-title">{finale.title}</h2>
                <p>{finale.lead}</p>
              </div>
              <div className="hub-finale__doors">
                <a
                  className="hub-door hub-door--candidate"
                  href={firstRoundPublicUrl()}
                  onClick={() => {
                    choose("candidate");
                    trackEvent("cta_clicked", {
                      cta_id: "hub_candidate_finale",
                      cta_location: "hub_finale",
                      destination: ctaDestination(firstRoundPublicUrl()),
                    });
                  }}
                >
                  <span>
                    <small>I'm preparing for a job</small>
                    <strong>Practise at First Round</strong>
                  </span>
                  <ArrowUpRight size={26} aria-hidden="true" />
                </a>
                <a
                  className="hub-door hub-door--recruiter"
                  href={apex25HomeUrl()}
                  onClick={() => {
                    choose("recruiter");
                    trackEvent("cta_clicked", {
                      cta_id: "hub_recruiter_finale",
                      cta_location: "hub_finale",
                      destination: ctaDestination(apex25HomeUrl()),
                    });
                  }}
                >
                  <span>
                    <small>I'm hiring</small>
                    <strong>See Apex 25</strong>
                  </span>
                  <ArrowUpRight size={26} aria-hidden="true" />
                </a>
                <Link to="/job-seekers" className="hub-more hub-more--light">
                  Or explore everything for job seekers
                </Link>
              </div>
            </div>
          </section>
        </main>
        <HubFooter />
      </div>
    </MotionConfig>
  );
};
